import BigNumber from "bignumber.js";
import { WalletProvicer } from "./WalletProvider";
import * as bip39 from "bip39"
import { WalletInfo } from "../WalletService";
import {
    Connection, clusterApiUrl, LAMPORTS_PER_SOL, Keypair, PublicKey,
    Transaction, sendAndConfirmTransaction, SystemProgram,
} from '@solana/web3.js';
import * as util from 'ethereumjs-util'
import { derivePath } from 'ed25519-hd-key';
import DataCenter, { Calculator, QuoteCoinID } from "../DataCenter";
import { CoinType } from "../../conf/Config";
import {
    getAccount, getAssociatedTokenAddress, createTransferInstruction, getOrCreateAssociatedTokenAccount
    , TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID
} from "@solana/spl-token";
import { Events, JSEvent } from "../../utils/JSEvent";

export class SOLWalletProvider extends WalletProvicer {
    #owner = null;
    #connection = null;
    constructor(blockchain) {
        super(blockchain);
        this.#connection = new Connection(clusterApiUrl(blockchain.url), 'confirmed');
    }

    /**
     * @param {Token} token 
     * @returns {Promise<BigNumber>}
     */
    balanceOf(token) {
        return new Promise(async (resolve, reject) => {
            if (this.#connection == null) {
                reject(this.makeError("web3 is null"))
            } else {
                if (token.name == "SOL") {
                    const balance = await this.#connection.getBalance(this.#owner.publicKey);
                    const v = Calculator.toNumber(balance, token.id);
                    resolve(v);
                } else {
                    const associate = await getAssociatedTokenAddress(new PublicKey(token.address), this.#owner.publicKey);
                    try {
                        const info = await getAccount(this.#connection, associate);
                        const v = Calculator.toNumber(info.amount, token.id)
                        resolve(v);
                    } catch (e) {
                        console.warn("token account not found for " + this.#owner.address())
                        resolve(new BigNumber(0));
                    }
                }
            }
        })
    }

    importMnemonic(mnemonic) {
        return new Promise((resolve, reject) => {
            bip39.mnemonicToSeed(mnemonic).then(seed => {
                if (!bip39.validateMnemonic(mnemonic)) {
                    reject({ code: -1, msg: "mnemonic format error" })
                    return;
                }

                const path = "m/44'/501'/0'/0'";
                const derivedSeed = derivePath(path, seed.toString("hex")).key;
                const owner = Keypair.fromSeed(derivedSeed);

                const wi = new WalletInfo();
                wi.mnemonic = mnemonic;
                wi.privateKey = util.bufferToHex(owner.secretKey);
                wi.publicKey = owner.publicKey.toBase58();
                wi.address = owner.publicKey.toBase58();
                wi.owner = owner;
                resolve(wi);
            })

        })
    }

    onWalletLoaded(walletInfo) {
        return new Promise((resolve) => {
            this.#owner = Keypair.fromSecretKey(util.toBuffer(walletInfo.privateKey))
            this.#connection.onAccountChange(this.#owner.publicKey, (accountInfo, context) => {
                JSEvent.emit(Events.Wallet.WalletAccountChanged, DataCenter.getCoin(CoinType.SOL));
            })

            DataCenter.getCoins().forEach(async coin => {
                if (coin.id == CoinType.SOL)
                    return;
                const associate = await getAssociatedTokenAddress(new PublicKey(coin.address), this.#owner.publicKey);
                this.#connection.onAccountChange(associate, (accountInfo, context) => {
                    JSEvent.emit(Events.Wallet.WalletAccountChanged, coin);
                })
            })
        })
    }

    isValidAddress(address) {
        try {
            new PublicKey(address);
            // this.#connection.getAccountInfo(a).then(a=>{
            //     console.log(a);
            // })
        } catch (e) {
            return false;
        }
        return true;
    }

    calcGasFee(gasPrice, gasUsed, quoteCoin) {
        const p = gasPrice;
        const p1 = Calculator.toNumber(p, QuoteCoinID);
        const p2 = p1.toTrimed(2, quoteCoin.showDecimals);
        return { bn: p1, str: p2 }
    }

    async transfer(walletTxInfo) {

        const to = walletTxInfo.txTo;
        const amount = walletTxInfo.coinType == CoinType.SOL ?
            walletTxInfo.amount - walletTxInfo.gasPrice : walletTxInfo.amount;
        const token = DataCenter.getCoin(walletTxInfo.coinType);

        let toPub = to;

        if (typeof to == "string") {
            toPub = new PublicKey(to);
        }

        let trans = null;
        if (token.id == CoinType.SOL) {
            const instructions = SystemProgram.transfer({
                fromPubkey: this.#owner.publicKey,
                toPubkey: toPub,
                lamports: amount
            })

            trans = new Transaction().add(instructions);
        } else {
            const mint = new PublicKey(token.address);
            const fromTokenAccount = await getOrCreateAssociatedTokenAccount(
                this.#connection, this.#owner, mint, this.#owner.publicKey
            );
            const toTokenAccount = await getOrCreateAssociatedTokenAccount(
                this.#connection, this.#owner, mint, toPub
            );
            const instruction = createTransferInstruction(fromTokenAccount.address,
                toTokenAccount.address, this.#owner.publicKey, amount,
                [], TOKEN_PROGRAM_ID);
            trans = new Transaction().add(instruction);
        }

        const sign = await this.#connection.sendTransaction(trans, [this.#owner]);
        walletTxInfo.transactionHash = sign;

        JSEvent.emit(Events.Wallet.PenddingTransaction, { token: token, walletTxInfo: walletTxInfo })

        this.#connection.onSignature(sign, (result, context) => {
            JSEvent.emit(Events.Wallet.Transaction, { token: token, trans: trans, walletTxInfo: walletTxInfo });
        }, "confirmed")

        //const sign = await sendAndConfirmTransaction(this.#connection, trans, [this.#owner]);
        return sign;
    }

    async confirmTransaction(signature) {
        const status = (await this.#connection.confirmTransaction(signature)).value;
        const trans = await this.#connection.getTransaction(signature);

        return status;
    }

    async transferToken(to, token, amount) {

    }

    /**
     * 返回对应区块链网络的浏览地址
     * @param {"tx"|"address"} item 
     * @param {string} value 
     */
    getExplorer(item, value) {
        return {
            name: "Solana Explorer",
            url: `https://explorer.solana.com/${item}/${value}?cluster=${this.blockchain.url}`
        };
    }
}